import { useCallback, useEffect, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { useDistributorWorkspace } from '@/contexts/DistributorWorkspaceContext'
import {
  fetchMyDistributorAffiliates,
  formatMyDistributorCurrency,
} from '@/lib/my-distributor'
import { RequireDistributorWorkspace } from '@/pages/SouDistribuidorPage/RequireDistributorWorkspace'
import type { MyDistributorAffiliateRegistration } from '@/types/my-distributor'
import styles from './SouDistribuidorPage.module.css'

function SouDistribuidorAfiliadosSaldoPageContent() {
  const { selectedProgram, campaignId } = useDistributorWorkspace()
  const [registrations, setRegistrations] = useState<MyDistributorAffiliateRegistration[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!selectedProgram) return
    setIsLoading(true)
    setError(null)
    try {
      const res = await fetchMyDistributorAffiliates(selectedProgram.distributor_id, {
        campaign_id: campaignId ?? undefined,
      })
      setRegistrations(res.registrations)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao carregar afiliados.')
    } finally {
      setIsLoading(false)
    }
  }, [selectedProgram, campaignId])

  useEffect(() => {
    void load()
  }, [load])

  const summary = useMemo(() => {
    const active = registrations.filter((registration) => registration.is_active)
    const totalSales = active.reduce(
      (sum, registration) => sum + (registration.metrics?.total_sales ?? 0),
      0
    )
    const totalPayments = active.reduce(
      (sum, registration) => sum + (registration.metrics?.payments_count ?? 0),
      0
    )
    return { activeCount: active.length, totalSales, totalPayments }
  }, [registrations])

  if (isLoading) {
    return (
      <div className={styles.centered}>
        <Icon icon="mdi:loading" className={styles.spinner} />
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.centered}>
        <span>{error}</span>
        <button type="button" className={styles.retryBtn} onClick={() => void load()}>
          Tentar novamente
        </button>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.statsGridSecondary}>
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Afiliados ativos</h3>
          <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: 0 }}>
            {summary.activeCount}
          </p>
        </div>
        <div className={styles.card}>
          <h3 className={styles.cardTitle}>Vendas dos afiliados</h3>
          <p style={{ fontSize: 'var(--font-size-xl)', fontWeight: 700, margin: 0 }}>
            {formatMyDistributorCurrency(summary.totalSales)}
          </p>
          <p className={styles.programMeta} style={{ marginTop: 'var(--spacing-sm)' }}>
            {summary.totalPayments} pagamento(s) no contexto selecionado
          </p>
        </div>
      </div>

      <div className={styles.card}>
        <h3 className={styles.cardTitle}>Resumo por afiliado</h3>
        {registrations.length === 0 ? (
          <div className={styles.emptyBlock}>
            <span>Nenhum afiliado credenciado neste contexto.</span>
          </div>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Afiliado</th>
                  <th>Código</th>
                  <th>Comissão</th>
                  <th>Status</th>
                  <th>Vendas</th>
                  <th>Total vendido</th>
                </tr>
              </thead>
              <tbody>
                {registrations.map((registration) => (
                  <tr key={registration.id}>
                    <td>{registration.affiliate_name}</td>
                    <td className={styles.trackingCode}>{registration.tracking_code}</td>
                    <td>{registration.commission_percent}%</td>
                    <td>
                      <span
                        className={
                          registration.is_active ? styles.badgeActive : styles.badgeInactive
                        }
                      >
                        {registration.is_active ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td>{registration.metrics?.payments_count ?? 0}</td>
                    <td>
                      {formatMyDistributorCurrency(registration.metrics?.total_sales ?? 0)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className={styles.programMeta} style={{ marginTop: 'var(--spacing-md)' }}>
          O saldo manual de cada afiliado é liquidado pela instituição. O extrato consolidado do
          distribuidor (incluindo movimentos dos afiliados) está na aba Saldo principal.
        </p>
      </div>
    </div>
  )
}

export function SouDistribuidorAfiliadosSaldoPage() {
  return (
    <RequireDistributorWorkspace requireActiveProgram>
      <SouDistribuidorAfiliadosSaldoPageContent />
    </RequireDistributorWorkspace>
  )
}
